import { api } from './api';

export const catalogApi = api.injectEndpoints({
  tagTypes: ['Catalog', 'CatalogItem'],
  endpoints: (builder) => ({
    // Get seller's catalog with enhanced parameters
    getSellerCatalog: builder.query({
      query: ({
        sellerId,
        page = 1,
        limit = 10,
        status,
        category,
        search,
        brand,
        sortBy = 'createdAt',
        sortOrder = 'desc',
        minPrice,
        maxPrice,
        inStock,
      }) => ({
        url: `/catalog/sellers/${sellerId}`,
        params: {
          page,
          limit,
          status,
          category,
          search,
          brand,
          sortBy,
          sortOrder,
          minPrice,
          maxPrice,
          inStock: inStock ? 'true' : undefined,
        },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: 'CatalogItem', id: _id })),
              { type: 'Catalog', id: 'LIST' },
            ]
          : [{ type: 'Catalog', id: 'LIST' }],
      transformResponse: (response) => ({
        data: response.data,
        pagination: response.pagination,
      }),
    }),

    // Get single catalog item
    getCatalogItem: builder.query({
      query: (id) => `/catalog/items/${id}`,
      providesTags: (result, error, id) => [{ type: 'CatalogItem', id }],
      transformResponse: (response) => response.data,
    }),

    addToCatalog: builder.mutation({
      query: (formData) => ({
        url: '/catalog/items',
        method: 'POST',
        body: { ...formData },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [{ type: 'Catalog', id: 'LIST' }],
    }),

    // Update catalog item with partial updates
    updateCatalogItem: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/catalog/items/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CatalogItem', id },
        { type: 'Catalog', id: 'LIST' },
      ],
    }),

    // Batch add to catalog with improved structure
    batchAddToCatalog: builder.mutation({
      query: ({ sellerId, items }) => ({
        url: '/catalog/items/batch',
        method: 'POST',
        body: {
          sellerId,
          items: items.map((item) => ({
            productId: item.productId,
            sellerPrice: Number(item.sellerPrice),
            discountPercentage: Number(item.discountPercentage) || 0,
            moq: Number(item.moq) || 1,
            stock: Number(item.stock) || 0,
            ...item,
          })),
        },
      }),
      invalidatesTags: [{ type: 'Catalog', id: 'LIST' }],
    }),

    // Delete catalog item (soft delete)
    deleteCatalogItem: builder.mutation({
      query: ({ id, permanent }) => ({
        url: `/catalog/items/${id}`,
        method: 'DELETE',
        params: { permanent },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'CatalogItem', id },
        { type: 'Catalog', id: 'LIST' },
      ],
    }),

    // Update stock levels
    updateStock: builder.mutation({
      query: ({ id, quantity, operation = 'set' }) => ({
        url: `/catalog/items/${id}/stock`,
        method: 'PUT',
        body: { quantity, operation }, // operation: 'set', 'add', or 'subtract'
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CatalogItem', id },
        { type: 'Catalog', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetSellerCatalogQuery,
  useLazyGetSellerCatalogQuery,
  useGetCatalogItemQuery,
  useAddToCatalogMutation,
  useUpdateCatalogItemMutation,
  useBatchAddToCatalogMutation,
  useDeleteCatalogItemMutation,
  useUpdateStockMutation,
} = catalogApi;
